import React, { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useDebouncedValue } from "@UILibrary/hooks/useDebouncedValue"

export type ClientOption = { id: number; name: string }

type Props = {
  queryKey: string
  // Server-side client search. Same shape as the clients index endpoint.
  fetcher: (q: string) => Promise<{ data: { id: string; attributes: { fullName: string } }[] }>
  selected: ClientOption[]
  onChange: (next: ClientOption[]) => void
  placeholder?: string
}

// Type-ahead multi-select for clients. Mirrors SearchableSelect but keeps a
// list of chosen clients as removable chips — used to pick which clients a
// paraplanner may (or may not) see under the allow_some / deny_some strategies.
export const ClientMultiSelect: React.FC<Props> = ({ queryKey, fetcher, selected, onChange, placeholder = "Search clients by name" }) => {
  const [input, setInput] = useState("")
  const [open, setOpen] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounced = useDebouncedValue(input)

  // Cancel a pending blur-close if the component unmounts inside the 150ms window.
  useEffect(() => () => {
    if (blurTimer.current) clearTimeout(blurTimer.current)
  }, [])

  const { data, isFetching, isError } = useQuery(
    [queryKey, debounced],
    () => fetcher(debounced),
    { enabled: open, keepPreviousData: true }
  )

  const selectedIds = new Set(selected.map((s) => s.id))
  const options: ClientOption[] = (data?.data ?? [])
    .map((row) => ({ id: Number(row.id), name: row.attributes.fullName }))
    .filter((opt) => !selectedIds.has(opt.id))

  const add = (opt: ClientOption) => {
    onChange([...selected, opt])
    setInput("")
  }

  const remove = (id: number) => onChange(selected.filter((s) => s.id !== id))

  return (
    <div className="fsp-admin__multiselect">
      {selected.length > 0 && (
        <div className="fsp-admin__chips">
          {selected.map((s) => (
            <span key={s.id} className="fsp-admin__chip">
              {s.name}
              <button type="button" className="fsp-admin__chip-remove" onClick={() => remove(s.id)} aria-label={`Remove ${s.name}`}>×</button>
            </span>
          ))}
        </div>
      )}

      <div className="fsp-admin__typeahead">
        <input
          type="search"
          className="wb-clients__search-input"
          placeholder={placeholder}
          value={input}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150)
          }}
          onChange={(e) => {
            setInput(e.target.value)
            setOpen(true)
          }}
        />
        {open && (
          <ul
            className="fsp-admin__typeahead-menu"
            // Keep the press from moving focus at all. Clearing the blur timer
            // afterwards was a race: mousedown moves focus first, so a press held
            // longer than the 150ms close delay unmounted the menu before mouseup —
            // the click then landed on whatever sat underneath (in the assign modal,
            // the submit button), and the option was never chosen.
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault()
              if (blurTimer.current) clearTimeout(blurTimer.current)
            }}
          >
            {isFetching && <li className="fsp-admin__typeahead-empty">Searching…</li>}
            {!isFetching && isError && <li className="fsp-admin__typeahead-empty">Search failed — try again</li>}
            {!isFetching && !isError && options.length === 0 && <li className="fsp-admin__typeahead-empty">No matches</li>}
            {options.map((opt) => (
              <li key={opt.id}>
                <button type="button" className="fsp-admin__typeahead-option" onClick={() => add(opt)}>
                  {opt.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
